import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark, Heart, Star, Calendar, Trash2, Search, SortAsc, Clock, BookOpen, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { imageUrl, stripImageBase } from '../config/images';

const Watchlist = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('watchlist');
  const [watchlist, setWatchlist] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showNotification, setShowNotification] = useState('');

  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Show notification helper
  const showNotificationMessage = (message) => {
    setShowNotification(message);
    setTimeout(() => setShowNotification(''), 4000);
  };

  // Fetch user's watchlist, favorites, and bookmarks
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated) return;

      try {
        const token = localStorage.getItem('videbits-token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch watchlist and favorites from API
        const [watchlistResponse, favoritesResponse] = await Promise.all([
          fetch(`${API_BASE}/watchlist`, { headers }),
          fetch(`${API_BASE}/favorites`, { headers })
        ]);

        // Check for session expiration
        if (watchlistResponse.status === 401 || favoritesResponse.status === 401) {
          showNotificationMessage('Session expired. Please log in again.');
          setTimeout(() => logout(), 2000);
          return;
        }

        if (watchlistResponse.ok) {
          const watchlistData = await watchlistResponse.json();
          setWatchlist(watchlistData.watchlist || []);
        }

        if (favoritesResponse.ok) {
          const favoritesData = await favoritesResponse.json();
          setFavorites(favoritesData.favorites || []);
        }

        // Fetch bookmarks from localStorage
        try {
          const bookmarksData = JSON.parse(localStorage.getItem('videbits-bookmarks') || '[]');
          // Convert to match the API format for consistency
          const formattedBookmarks = bookmarksData.map((bookmark, index) => ({
            id: index,
            movieId: bookmark.id,
            movieTitle: bookmark.title,
            posterPath: stripImageBase(bookmark.poster_path),
            releaseDate: bookmark.release_date,
            voteAverage: bookmark.vote_average,
            addedAt: bookmark.addedAt || new Date().toISOString() // Use stored timestamp or current time
          }));
          setBookmarks(formattedBookmarks);
        } catch (error) {
          console.error('Error loading bookmarks from localStorage:', error);
          setBookmarks([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [isAuthenticated, API_BASE]);

  // Remove from watchlist. Insider titles (no tmdb_id) are graph follows keyed by
  // the project id, so they use the follow-delete endpoint instead of /watchlist/{tmdbId}.
  const removeFromWatchlist = async (item) => {
    try {
      const token = localStorage.getItem('videbits-token');
      const url = item.isInsider
        ? `${API_BASE}/api/watchlist/title/${item.projectId}`
        : `${API_BASE}/watchlist/${item.movieId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        showNotificationMessage('Session expired. Please log in again.');
        setTimeout(() => logout(), 2000);
        return;
      }

      if (response.ok) {
        setWatchlist(watchlist.filter((w) => w.id !== item.id));
      }
    } catch (error) {
      console.error('Failed to remove from watchlist:', error);
    }
  };

  // Remove from favorites
  const removeFromFavorites = async (movieId) => {
    try {
      const token = localStorage.getItem('videbits-token');
      const response = await fetch(`${API_BASE}/favorites/${movieId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        showNotificationMessage('Session expired. Please log in again.');
        setTimeout(() => logout(), 2000);
        return;
      }

      if (response.ok) {
        setFavorites(favorites.filter(item => item.movieId !== movieId));
      }
    } catch (error) {
      console.error('Failed to remove from favorites:', error);
    }
  };

  // Remove from bookmarks (localStorage)
  const removeFromBookmarks = (movieId) => {
    try {
      const bookmarksData = JSON.parse(localStorage.getItem('videbits-bookmarks') || '[]');
      const updatedBookmarks = bookmarksData.filter(item => item.id.toString() !== movieId.toString());
      localStorage.setItem('videbits-bookmarks', JSON.stringify(updatedBookmarks));
      
      // Update the state
      setBookmarks(bookmarks.filter(item => item.movieId.toString() !== movieId.toString()));
    } catch (error) {
      console.error('Failed to remove from bookmarks:', error);
    }
  };

  // Filter and sort items
  const filterAndSort = (items) => {
    let filtered = items.filter(item =>
      item.movieTitle.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt));
        break;
      case 'title':
        filtered.sort((a, b) => a.movieTitle.localeCompare(b.movieTitle));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.voteAverage || 0) - (a.voteAverage || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'watchlist':
        return watchlist;
      case 'favorites':
        return favorites;
      case 'bookmarks':
        return bookmarks;
      default:
        return watchlist;
    }
  };

  const currentItems = getCurrentItems();
  const filteredItems = filterAndSort(currentItems);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Bookmark className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view your watchlist</h2>
          <p className="text-gray-600 mb-6">Create an account to save movies and build your personal watchlist</p>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Message */}
      {showNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in">
          {showNotification}
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-900">Watchlist</h1>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Welcome, {user?.first_name}!</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'watchlist', label: 'Watchlist', icon: Bookmark, count: watchlist.length }
            ].map(({ id, label, icon: Icon, count }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-videbits-600 text-videbits-600'
                    : 'border-transparent text-videbits-500 hover:text-videbits-700 hover:border-videbits-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{label}</span>
                <span className="bg-videbits-100 px-2 py-1 rounded-full text-xs">{count}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={`Search your ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-videbits-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <SortAsc className="w-4 h-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-videbits-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Alphabetical</option>
              <option value="rating">Highest Rated</option>
            </select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-videbits-600 mb-4"></div>
            <p className="text-gray-500">Loading your watchlist...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {activeTab === 'watchlist' ? (
                <Bookmark className="w-10 h-10 text-gray-400" />
              ) : activeTab === 'favorites' ? (
                <Heart className="w-10 h-10 text-gray-400" />
              ) : (
                <BookOpen className="w-10 h-10 text-gray-400" />
              )}
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Your {activeTab} {activeTab === 'bookmarks' ? 'are' : 'is'} empty
            </h3>
            <p className="text-gray-500 mb-6">
              {activeTab === 'watchlist' 
                ? 'Start building your watchlist by adding movies you want to watch later'
                : activeTab === 'favorites'
                ? 'Mark movies as favorites to save them to your watchlist'
                : 'Bookmark movies to quickly access them later'
              }
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors"
            >
              <Search className="w-4 h-4 mr-2" />
              Discover Movies
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(item.isInsider ? `/insider/project/${item.projectId}` : (item.category === 'TV Series' ? `/tv/${item.movieId}` : `/movie/${item.movieId}`))}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="aspect-w-2 aspect-h-3 relative">
                  {item.posterPath ? (
                    <img
                      src={imageUrl(item.posterPath, 'w500')}
                      alt={item.movieTitle}
                      className="w-full h-64 object-cover"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
                      <ExternalLink className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Overlay action — remove (the card itself opens the title) */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeTab === 'watchlist') {
                          removeFromWatchlist(item);
                        } else if (activeTab === 'favorites') {
                          removeFromFavorites(item.movieId);
                        } else {
                          removeFromBookmarks(item.movieId);
                        }
                      }}
                      aria-label="Remove"
                      className="flex items-center justify-center w-12 h-12 bg-red-500 bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {item.movieTitle}
                  </h3>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{item.releaseDate ? new Date(item.releaseDate).getFullYear() : 'N/A'}</span>
                    </div>
                    {item.voteAverage && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>{item.voteAverage.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>Added {formatDate(item.addedAt)}</span>
                    </div>
                    {activeTab === 'watchlist' ? (
                      <Bookmark className="w-4 h-4 text-videbits-500" />
                    ) : activeTab === 'favorites' ? (
                      <Heart className="w-4 h-4 text-red-500" />
                    ) : (
                      <BookOpen className="w-4 h-4 text-videbits-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Watchlist;
